import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Paperclip, SendHorizontal, X } from 'lucide-react';
import {
  fetchProjectMessages,
  isImageAttachment,
  sendProjectMessage,
  uploadChatAttachments,
} from '../../utils/projectChat.js';
import '../../component-styles/ChatSection.css';

const AVATAR_COLORS = ['#62794E', '#3E6B8A', '#8A5A3E', '#6B4E8A', '#8A3E5A', '#3E8A6E'];

function getInitials(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function colorFor(key) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatTime(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

export default function ChatSection() {
  const { projectId } = useOutletContext();
  const userId = localStorage.getItem('userId');
  const rawUserId = localStorage.getItem('ownUserId') || userId;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState([]);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  async function loadMessages({ silent = false } = {}) {
    if (!userId || !projectId) return;
    if (!silent) setLoading(true);

    try {
      const data = await fetchProjectMessages(userId, projectId);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load project chat');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadMessages({ silent: true });
    }, 10000);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const hasContent = useMemo(() => draft.trim() || attachments.length > 0, [draft, attachments]);

  async function handleFilesSelected(event) {
    const files = event.target.files;
    if (!files?.length) return;

    try {
      setUploading(true);
      const uploaded = await uploadChatAttachments(files);
      setAttachments((prev) => [...prev, ...uploaded]);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to upload attachment');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  function removeAttachment(attachmentId) {
    setAttachments((prev) => prev.filter((item) => item.id !== attachmentId));
  }

  function handleComposerKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    handleSubmit(event);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (sending || uploading) return;

    const body = draft.trim();
    if (!body && attachments.length === 0) return;

    try {
      setSending(true);
      const data = await sendProjectMessage(userId, projectId, { body, attachments });
      if (data.message) setMessages((prev) => [...prev, data.message]);
      setDraft('');
      setAttachments([]);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="project-chat-section">
      <div className="project-chat-messages" ref={scrollRef}>
        {loading ? (
          <p className="project-chat-empty">Loading conversation…</p>
        ) : messages.length === 0 ? (
          <p className="project-chat-empty">
            No messages yet. Start the conversation about this project.
          </p>
        ) : (
          messages.map((message) => {
            const isOwn = message.authorUserId === rawUserId;
            return (
              <article
                key={message.messageId}
                className={`project-chat-message ${isOwn ? 'own' : 'other'}`}
              >
                {!isOwn ? (
                  <div
                    className="project-chat-avatar"
                    style={{ background: colorFor(message.authorEmail || message.authorName || '') }}
                    title={message.authorName}
                  >
                    {getInitials(message.authorName)}
                  </div>
                ) : null}
                <div className="project-chat-bubble-col">
                  {!isOwn ? <span className="project-chat-author">{message.authorName}</span> : null}
                  <div className="project-chat-bubble">
                    {message.body ? <p>{message.body}</p> : null}
                    {Array.isArray(message.attachments) && message.attachments.length > 0 ? (
                      <div className="project-chat-attachments">
                        {message.attachments.map((attachment) =>
                          isImageAttachment(attachment) ? (
                            <a
                              key={attachment.id || attachment.url}
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              className="project-chat-attachment-image"
                            >
                              <img src={attachment.url} alt={attachment.name || 'Attachment'} />
                            </a>
                          ) : (
                            <a
                              key={attachment.id || attachment.url}
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              className="project-chat-attachment-file"
                            >
                              <Paperclip size={14} />
                              <span>{attachment.name || 'Download file'}</span>
                            </a>
                          )
                        )}
                      </div>
                    ) : null}
                  </div>
                  <time className="project-chat-meta">{formatTime(message.createdAt)}</time>
                </div>
              </article>
            );
          })
        )}
      </div>

      <form className="project-chat-composer" onSubmit={handleSubmit}>
        {attachments.length > 0 ? (
          <div className="project-chat-pending">
            {attachments.map((attachment) => (
              <button
                type="button"
                key={attachment.id}
                className="project-chat-chip"
                onClick={() => removeAttachment(attachment.id)}
              >
                {attachment.name}
                <X size={12} />
              </button>
            ))}
          </div>
        ) : null}

        <textarea
          rows={3}
          placeholder="Message your team about this project…"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleComposerKeyDown}
        />

        {error ? <p className="project-chat-status error">{error}</p> : null}

        <div className="project-chat-actions">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleFilesSelected}
          />
          <button
            type="button"
            className="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || sending}
          >
            <Paperclip size={15} />
            {uploading ? 'Uploading…' : 'Attach file'}
          </button>
          <button type="submit" disabled={sending || uploading || !hasContent}>
            <SendHorizontal size={16} />
          </button>
        </div>
      </form>
    </div>
  );
}
